import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LocationContext = createContext(null);

// Louisiana parishes with their approximate coordinates
const LOUISIANA_PARISHES = {
  "Acadia": { lat: 30.2952, lng: -92.4142 },
  "Allen": { lat: 30.6529, lng: -92.8277 },
  "Ascension": { lat: 30.2110, lng: -90.9079 },
  "Assumption": { lat: 29.8999, lng: -91.0565 },
  "Avoyelles": { lat: 31.0755, lng: -92.0027 },
  "Beauregard": { lat: 30.6494, lng: -93.3416 },
  "Bienville": { lat: 32.3469, lng: -93.0510 },
  "Bossier": { lat: 32.6782, lng: -93.6016 },
  "Caddo": { lat: 32.5815, lng: -93.8893 },
  "Calcasieu": { lat: 30.2296, lng: -93.3578 },
  "Caldwell": { lat: 32.0908, lng: -92.1154 },
  "Cameron": { lat: 29.8037, lng: -93.3247 },
  "Catahoula": { lat: 31.6685, lng: -91.8513 },
  "Claiborne": { lat: 32.8260, lng: -92.9953 },
  "Concordia": { lat: 31.4457, lng: -91.5078 },
  "De Soto": { lat: 32.0544, lng: -93.7347 },
  "East Baton Rouge": { lat: 30.5388, lng: -91.0932 },
  "East Carroll": { lat: 32.7318, lng: -91.2371 },
  "East Feliciana": { lat: 30.8385, lng: -91.0356 },
  "Evangeline": { lat: 30.7274, lng: -92.4062 },
  "Franklin": { lat: 32.1324, lng: -91.6751 },
  "Grant": { lat: 31.5831, lng: -92.5568 },
  "Iberia": { lat: 29.8024, lng: -91.7993 },
  "Iberville": { lat: 30.2577, lng: -91.3499 },
  "Jackson": { lat: 32.3004, lng: -92.5568 },
  "Jefferson": { lat: 29.8286, lng: -90.1021 },
  "Jefferson Davis": { lat: 30.2641, lng: -92.8131 },
  "Lafayette": { lat: 30.2241, lng: -92.0198 },
  "Lafourche": { lat: 29.5547, lng: -90.3981 },
  "La Salle": { lat: 31.6768, lng: -92.1777 },
  "Lincoln": { lat: 32.6015, lng: -92.6640 },
  "Livingston": { lat: 30.4407, lng: -90.7279 },
  "Madison": { lat: 32.4038, lng: -91.2371 },
  "Morehouse": { lat: 32.7902, lng: -91.8037 },
  "Natchitoches": { lat: 31.7607, lng: -93.0867 },
  "Orleans": { lat: 29.9511, lng: -90.0715 },
  "Ouachita": { lat: 32.4777, lng: -92.1563 },
  "Plaquemines": { lat: 29.4552, lng: -89.6968 },
  "Pointe Coupee": { lat: 30.7099, lng: -91.6001 },
  "Rapides": { lat: 31.2013, lng: -92.5317 },
  "Red River": { lat: 32.0935, lng: -93.3416 },
  "Richland": { lat: 32.4179, lng: -91.7576 },
  "Sabine": { lat: 31.5648, lng: -93.5548 },
  "St. Bernard": { lat: 29.8752, lng: -89.8252 },
  "St. Charles": { lat: 29.9058, lng: -90.3566 },
  "St. Helena": { lat: 30.8213, lng: -90.6876 },
  "St. James": { lat: 30.0258, lng: -90.8029 },
  "St. John the Baptist": { lat: 30.0591, lng: -90.4787 },
  "St. Landry": { lat: 30.5874, lng: -92.0027 },
  "St. Martin": { lat: 30.1263, lng: -91.6001 },
  "St. Mary": { lat: 29.6299, lng: -91.4999 },
  "St. Tammany": { lat: 30.4085, lng: -89.9540 },
  "Tangipahoa": { lat: 30.6268, lng: -90.4038 },
  "Tensas": { lat: 32.0016, lng: -91.3384 },
  "Terrebonne": { lat: 29.4402, lng: -90.8359 },
  "Union": { lat: 32.8316, lng: -92.3762 },
  "Vermilion": { lat: 29.8474, lng: -92.2896 },
  "Vernon": { lat: 31.1088, lng: -93.1843 },
  "Washington": { lat: 30.8524, lng: -90.0386 },
  "Webster": { lat: 32.7180, lng: -93.3350 },
  "West Baton Rouge": { lat: 30.4460, lng: -91.3132 },
  "West Carroll": { lat: 32.7902, lng: -91.4575 },
  "West Feliciana": { lat: 30.8496, lng: -91.4265 },
  "Winn": { lat: 31.9433, lng: -92.6269 }
};

// Region mapping for volunteers
const REGION_MAPPING = {
  "hammond": ["Tangipahoa"],
  "northwest": ["Bienville", "Bossier", "Caddo", "Claiborne", "De Soto", "Jackson", "Lincoln", "Natchitoches", "Red River", "Sabine", "Webster"],
  "northeast": ["Caldwell", "East Carroll", "Franklin", "Madison", "Morehouse", "Ouachita", "Richland", "Tensas", "Union", "West Carroll"],
  "central": ["Avoyelles", "Catahoula", "Concordia", "Grant", "La Salle", "Rapides", "Vernon", "Winn"],
  "southwest": ["Allen", "Beauregard", "Calcasieu", "Cameron", "Jefferson Davis"],
  "acadiana": ["Acadia", "Evangeline", "Iberia", "Lafayette", "St. Landry", "St. Martin", "Vermilion"],
  "capital": ["Ascension", "East Baton Rouge", "East Feliciana", "Iberville", "Livingston", "Pointe Coupee", "St. Helena", "West Baton Rouge", "West Feliciana"],
  "southeast": ["Jefferson", "Orleans", "Plaquemines", "St. Bernard", "St. Charles", "St. James", "St. John the Baptist", "St. Tammany", "Tangipahoa", "Washington"],
  "bayou": ["Assumption", "Lafourche", "St. Mary", "Terrebonne"]
};

// Cookie helpers
const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; expires=${expires}; path=/; SameSite=Lax`;
};

const getCookie = (name) => {
  const value = document.cookie.split('; ').find(row => row.startsWith(name + '='));
  if (value) {
    try {
      return JSON.parse(decodeURIComponent(value.split('=')[1]));
    } catch {
      return null;
    }
  }
  return null;
};

// Find nearest parish from coordinates
const findNearestParish = (lat, lng) => {
  let nearestParish = null;
  let minDistance = Infinity;
  
  for (const [parish, coords] of Object.entries(LOUISIANA_PARISHES)) {
    const distance = Math.sqrt(
      Math.pow(lat - coords.lat, 2) + Math.pow(lng - coords.lng, 2)
    );
    if (distance < minDistance) {
      minDistance = distance;
      nearestParish = parish;
    }
  }
  
  return nearestParish;
};

// Find region from parish
const findRegionFromParish = (parish) => {
  for (const [region, parishes] of Object.entries(REGION_MAPPING)) {
    if (parishes.includes(parish)) {
      return region;
    }
  }
  return "southeast"; // Default to southeast (includes most urban areas)
};

export function LocationProvider({ children }) {
  const [location, setLocation] = useState({
    parish: null,
    region: null,
    coordinates: null,
    source: null, // 'gps', 'manual', 'cookie'
    loading: true,
    error: null,
    permissionDenied: false
  });

  // Detect location using browser geolocation
  const detectLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocation(prev => ({
        ...prev,
        loading: false,
        error: 'Geolocation not supported'
      }));
      return;
    }

    setLocation(prev => ({ ...prev, loading: true, error: null }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const parish = findNearestParish(latitude, longitude);
        const region = findRegionFromParish(parish);
        
        const newLocation = {
          parish,
          region,
          coordinates: { lat: latitude, lng: longitude },
          source: 'gps',
          loading: false,
          error: null,
          permissionDenied: false
        };
        
        setLocation(newLocation);
        setCookie('dowurk_location', {
          parish: newLocation.parish,
          region: newLocation.region,
          coordinates: newLocation.coordinates,
          source: 'gps'
        });
      },
      (error) => {
        console.log('Geolocation error:', error.message);
        setLocation(prev => ({
          ...prev,
          loading: false,
          error: error.message,
          permissionDenied: error.code === 1 // PERMISSION_DENIED
        }));
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 3600000 // Cache for 1 hour
      }
    );
  }, []);

  // Load location from cookie on mount
  useEffect(() => {
    const savedLocation = getCookie('dowurk_location');
    if (savedLocation && savedLocation.parish) {
      setLocation({
        ...savedLocation,
        source: 'cookie',
        loading: false,
        error: null
      });
    } else {
      // No saved location, try to detect
      detectLocation();
    }
  }, [detectLocation]);

  // Manually set location
  const setManualLocation = useCallback((parish, region = null) => {
    const detectedRegion = region || findRegionFromParish(parish);
    const parishCoords = LOUISIANA_PARISHES[parish];
    
    const newLocation = {
      parish,
      region: detectedRegion,
      coordinates: parishCoords || null,
      source: 'manual',
      loading: false,
      error: null,
      permissionDenied: false
    };
    
    setLocation(newLocation);
    setCookie('dowurk_location', {
      parish: newLocation.parish,
      region: newLocation.region,
      coordinates: newLocation.coordinates,
      source: 'manual'
    });
  }, []);

  // Clear saved location
  const clearLocation = useCallback(() => {
    document.cookie = 'dowurk_location=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    setLocation({
      parish: null,
      region: null,
      coordinates: null,
      source: null,
      loading: false,
      error: null,
      permissionDenied: false
    });
  }, []);

  // Get all parishes (for dropdowns)
  const getParishes = useCallback(() => {
    return Object.keys(LOUISIANA_PARISHES).sort();
  }, []);

  // Get parishes by region
  const getParishesByRegion = useCallback((region) => {
    return REGION_MAPPING[region] || [];
  }, []);

  // Get all regions
  const getRegions = useCallback(() => {
    return Object.keys(REGION_MAPPING);
  }, []);

  const value = {
    ...location,
    detectLocation,
    setManualLocation,
    clearLocation,
    getParishes,
    getParishesByRegion,
    getRegions,
    LOUISIANA_PARISHES,
    REGION_MAPPING
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}

export default LocationContext;
